import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { deleteJSON } from '@/lib/api'
import type { PlatformAssociationDetail } from './PlatformAssociationDetailPage'

// The API refuses with the codes its contract enumerates; anything else is shown as sent.
const refusalMessages: Record<string, string> = {
  demo_association_seeding: 'This association is seeding. Delete it when seeding finishes.',
  demo_association_required: 'Only demo associations can be deleted.',
}

export function PlatformDemoDelete({ association }: { association: PlatformAssociationDetail }) {
  const [open, setOpen] = useState(false)
  const [confirmation, setConfirmation] = useState('')
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const seeding = association.seed_status === 'seeding'
  const deletion = useMutation({
    mutationFn: () => deleteJSON(`/api/v1/platform/associations/${association.id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['platform-associations'] })
      navigate('/platform/associations', { replace: true })
    },
    // A refusal means the server saw a different association (e.g. seeding began); show its state.
    onError: () =>
      queryClient.invalidateQueries({ queryKey: ['platform-association', association.id] }),
  })
  const canConfirm =
    confirmation === association.web_address_name && !seeding && !deletion.isPending

  function changeOpen(next: boolean) {
    if (deletion.isPending) return
    setOpen(next)
    setConfirmation('')
    deletion.reset()
  }

  return (
    <section className="mt-8 border-t border-border pt-6">
      <h2 className="text-subheading font-semibold text-foreground">Delete demo association</h2>
      <p className="mb-3 text-caption text-muted-foreground">
        Removes the association and every row in it. This cannot be undone.
      </p>
      <Dialog open={open} onOpenChange={changeOpen}>
        <div className="flex flex-wrap items-center gap-3">
          <DialogTrigger asChild>
            <Button variant="quietDestructive" disabled={seeding}>
              <Trash2 className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Delete association
            </Button>
          </DialogTrigger>
          {seeding && (
            <span className="text-caption text-muted-foreground">
              Available when seeding finishes.
            </span>
          )}
        </div>
        <DialogContent closeDisabled={deletion.isPending}>
          <DialogHeader>
            <DialogTitle>Delete {association.name}</DialogTitle>
            <DialogDescription className="text-caption">
              Type {association.web_address_name} to confirm.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(event) => {
              event.preventDefault()
              if (canConfirm) deletion.mutate()
            }}
          >
            <Input
              aria-label={`Type ${association.web_address_name} to confirm`}
              value={confirmation}
              disabled={deletion.isPending}
              onChange={(event) => setConfirmation(event.target.value)}
            />
            {deletion.error && (
              <Alert className="mt-3">
                {refusalMessages[deletion.error.message] ?? deletion.error.message}
              </Alert>
            )}
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={deletion.isPending}>
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" variant="destructive" disabled={!canConfirm}>
                {deletion.isPending ? 'Deleting…' : 'Delete association'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  )
}
